/* eslint-disable no-unused-vars */
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import { getTransferInstruction } from 'api/transfers/service';
import { useParams } from 'react-router';
import { formatOption } from 'components/singleTransfer/SingleTransfer.utils';
import useLocale from 'components/useLocale';
import { BeforeAfterDayRadioComponent } from 'components/beforeAfterDayRadio/BeforeAfterDayRadio';
import usePlaceResolver from 'components/usePlaceResolver/usePlaceResolver';
import { ACCOUNT_TYPES } from 'constants.js';
import RecurringTransferWorkflow from './RecurringTransferWorkflow';
import { ModifyContext, ReadOnlyContext } from './contexts/ModifyRecurringTransfer.context';
import RecurringFormProvider from './contexts/RecurringFormProvider';
import {
  buildRecurrence,
  getFutureDated,
  TYPES,
  mapRecurrenceFrequencyTypeFromApi
} from './recurringTransfer.utils';

export default function ModifyRecurringTransfer() {
  const locale = useLocale();
  const { action, id: templateId } = useParams();
  const [modifying, setModifying] = useState();
  const [readOnly, setReadOnly] = useState();
  const services = useMemo(
    () => [getTransferInstruction({ templateId: parseInt(templateId, 0) })],
    [templateId]
  );
  const { Placeholder, results } = usePlaceResolver({ services });

  // Without useEffect, clicking on `modify` from `view` doesnt mount the modify component
  useEffect(() => {
    switch (action) {
      case 'modify':
        setModifying(true);
        setReadOnly(false);
        break;
      case 'view':
        setModifying(false);
        setReadOnly(true);
        break;
      default:
    }
  }, [action]);

  if (Placeholder) {
    return <Placeholder />;
  }

  const getInitialData = (results) => results?.['0'] || {};

  const getDateForDatePicker = (value) => {
    if (!value) return value;

    const date = moment({ ...value, month: value.javaMonth });

    // clear out date if its before today
    // only if its a modify page
    if (!readOnly && date.isBefore(moment().startOf('day'))) return undefined;

    return date;
  };

  const getAccountId = (account) => account?.id;

  const data = getInitialData(results);
  const schedulerType = mapRecurrenceFrequencyTypeFromApi(data);

  const mapApiToFormData = (data) => {
    return {
      creditAccount: [formatOption(locale)(ACCOUNT_TYPES.CREDIT)(data?.bookVO?.creditAccount)],
      creditAccountId: getAccountId(data?.bookVO?.creditAccount),
      debitAccount: [formatOption(locale)(ACCOUNT_TYPES.DEBIT)(data?.bookVO?.debitAccount)],
      debitAccountId: getAccountId(data?.bookVO?.debitAccount),
      name: data?.bookVO?.templateName,
      amount: data?.bookVO?.amount,
      memo: data?.bookVO?.memo,
      transferId: templateId,
      instructionId: data?.bookVO?.instruction?.id,
      futureDated: getFutureDated(data?.bookVO?.instruction?.backOrFutureDate),
      frequencyType: data?.bookVO?.instruction?.frequency || 'WEEK',
      startValue: getDateForDatePicker(data?.bookVO?.instruction?.startDate),
      scheduling: {
        startValue: getDateForDatePicker(data?.bookVO?.instruction?.startDate),
        endValue: getDateForDatePicker(data?.bookVO?.instruction?.endDate),
        ends: data?.bookVO?.instruction?.expires,
        type: schedulerType,
        recurrence: buildRecurrence(schedulerType)(data?.bookVO)
      }
    };
  };

  return (
    <ReadOnlyContext.Provider value={readOnly}>
      <ModifyContext.Provider value={modifying}>
        <RecurringFormProvider initialData={mapApiToFormData(data)}>
          {() => <RecurringTransferWorkflow />}
        </RecurringFormProvider>
      </ModifyContext.Provider>
    </ReadOnlyContext.Provider>
  );
}
